import { useEffect, useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { validateCustomButtonLink } from "./validateCustomButtonLink";

interface ListingAppearanceModalProps {
  opened: boolean;
  onClose: () => void;
  inputId: string;
  initialValue?: string | null;
  isSaving: boolean;
  onSave: (value: string, syncWithPms?: boolean) => void;
  pmsSync?: {
    syncWithPms: boolean;
    pmsValue?: string | null;
  };
}

export const ListingAppearanceModal = ({
  opened,
  onClose,
  inputId,
  initialValue,
  isSaving,
  onSave,
  pmsSync
}: ListingAppearanceModalProps) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [syncWithPms, setSyncWithPms] = useState(pmsSync?.syncWithPms ?? false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (opened) {
      setValue(initialValue ?? "");
      setSyncWithPms(pmsSync?.syncWithPms ?? false);
      setError(undefined);
    }
  }, [opened, initialValue, pmsSync?.syncWithPms]);

  const isSynced = Boolean(pmsSync) && syncWithPms;

  const handleSyncChange = (nextSyncWithPms: boolean) => {
    setSyncWithPms(nextSyncWithPms);
    setError(undefined);
    if (nextSyncWithPms) setValue(pmsSync?.pmsValue ?? "");
    else setValue(initialValue ?? "");
  };

  const handleSave = () => {
    if (isSynced) {
      onSave(value.trim(), true);
      return;
    }

    const trimmed = value.trim();
    const validationError = validateCustomButtonLink(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave(trimmed, pmsSync ? false : undefined);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-full max-w-[816px] overflow-auto rounded-lg"
    >
      <div className="border-b border-alpha-40 px-8 py-6">
        <div className="flex items-start justify-between gap-1">
          <h3 className="text-xl font-bold text-content-1000">
            Listing appearance
          </h3>
          <IconButton
            size={IconButtonSize.LG}
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            className="text-content-400 hover:text-content-500"
          />
        </div>
      </div>

      <div className="px-8 pt-6 pb-4 space-y-4">
        <div>
          <BaseInput
            label="Custom button link"
            id={inputId}
            placeholder="https://"
            value={value}
            onChange={(val) => {
              setValue(val);
              setError(undefined);
            }}
            error={error}
            disabled={isSynced}
            className="w-full"
          />
          {pmsSync ? (
            <PmsSyncOption
              className="!mt-4"
              name="listing-appearance-pms-sync"
              value={syncWithPms}
              onChange={handleSyncChange}
              disabled={isSaving}
              order="manual-first"
            />
          ) : null}
        </div>
        <div>
          <SaveCancelButtons
            onSave={handleSave}
            onCancel={onClose}
            saveLoading={isSaving}
          />
        </div>
      </div>
    </Modal>
  );
};
